import { client as isiaClient } from "@carnet/api/isia/client.gen";
import { client as backofficeClient } from "@carnet/api/backoffice/client.gen";
import { configureClient, type AuthAdapter } from "@carnet/api/runtime";
import { appendResponseHeader, deleteCookie, setResponseHeader, setResponseStatus } from "h3";

export default defineNuxtPlugin({
  name: "heyapi",
  parallel: true,
  async setup(nuxtApp) {
    const { session, clear } = useUserSession();
    const event = useRequestEvent();
    const requestHeaders = useRequestHeaders(["cookie"]);
    const config = useRuntimeConfig();
    const router = useRouter();
    // Cookie flash consommé après reload : SessionExpiredBanner le lit pour s'afficher
    // partout, et authenticated.ts le promeut en `reason=session-expired` quand il
    // redirige vers /login (URL partageable, query persistante après dismiss du banner).
    const sessionLostCookie = useCookie("session-just-expired", { maxAge: 10, path: "/", sameSite: "lax" });

    let isRefreshing = false;
    let refreshPromise: Promise<void> | null = null;
    let lastRefreshTime = 0;
    let sessionLost = false;
    const REFRESH_COOLDOWN_MS = 2000;

    const releaseSessionLossLatchOnReauth = (accessToken?: string) => {
      if (accessToken) sessionLost = false;
    };
    watch(() => session.value?.token?.accessToken, releaseSessionLossLatchOnReauth, { flush: "sync" });

    const sessionLossRedirectLocation = () => {
      const route = router.currentRoute.value;
      if (!isAuthenticatedRoute(route)) return route.fullPath;
      const redirect = safeRedirectPath(route.fullPath);
      return redirect ? `/login?redirect=${encodeURIComponent(redirect)}` : "/login";
    };

    // Termine la séquence quand la session est irrécupérable. Trois précautions :
    // - `clear()` (qui appelle useRequestFetch en interne) doit tourner dans le contexte
    //   Nuxt sinon `[nuxt] A composable that requires access to the Nuxt instance...`.
    // - `clear()` peut échouer silencieusement (DELETE round-trip qui hang en SSR), on
    //   force `session.value = null` en fallback pour que `loggedIn` bascule à false ;
    //   `deleteCookie(nuxt-session)` car clear() ne propage pas son set-cookie à la
    //   réponse SSR parente — sans ça le 302 re-part avec la session morte et boucle.
    // - En SSR, on set le 302 + Location sur le response parent puis on laisse le 401
    //   se propager normalement. Un `throw createError({ fatal: true })` ferait render
    //   error.vue, ce qui déclenche un nouveau cycle SSR et une boucle infinie.
    const handleSessionLoss = async () => {
      if (sessionLost) return;
      sessionLost = true;

      const lossExperiencedDuringClientNavigation = !event;
      if (lossExperiencedDuringClientNavigation) sessionLostCookie.value = "1";

      try {
        await nuxtApp.runWithContext(() => clear());
      } catch (e) {
        console.log("[heyapi] clear() threw", { message: (e as Error)?.message });
      }
      console.log("[heyapi] after clear", { hasToken: !!session.value?.token, hasUser: !!session.value?.user });
      session.value = null;

      if (event) {
        deleteCookie(event, "nuxt-session");
        setResponseStatus(event, 302);
        setResponseHeader(event, "Location", sessionLossRedirectLocation());
        return;
      }
      await nuxtApp.runWithContext(() => reloadNuxtApp({ persistState: false, force: true }));
    };

    const refreshToken = async (tokenAtTimeOf401?: string): Promise<void> => {
      if (sessionLost) {
        return;
      }

      console.log("[heyapi] refreshToken called", {
        tokenAtTimeOf401: tokenAtTimeOf401?.slice(0, 8),
        currentToken: session.value?.token?.accessToken?.slice(0, 8),
        hasToken: !!session.value?.token,
        hasUser: !!session.value?.user,
        isRefreshing,
        side: import.meta.server ? "server" : "client",
      });

      if (isRefreshing && refreshPromise) {
        console.log("[heyapi] mutex held, awaiting existing refresh");
        return refreshPromise;
      }

      if (tokenAtTimeOf401 && tokenAtTimeOf401 !== session.value?.token?.accessToken) {
        console.log("[heyapi] token changed since 401, skipping refresh");
        return;
      }

      if (!session.value?.token) {
        console.log("[heyapi] no local token, triggering session loss");
        await handleSessionLoss();
        return;
      }

      const timeSinceLastRefresh = Date.now() - lastRefreshTime;
      if (lastRefreshTime > 0 && timeSinceLastRefresh < REFRESH_COOLDOWN_MS) {
        console.log("[heyapi] cooldown active, skipping refresh");
        return;
      }

      isRefreshing = true;
      refreshPromise = (async () => {
        try {
          let newSession: typeof session.value;
          if (import.meta.server && event) {
            console.log("[heyapi] refresh via SSR $fetch.raw");
            const response = await $fetch.raw("/api/refresh-token", {
              method: "POST",
              headers: requestHeaders,
              timeout: 15_000,
            });

            for (const cookie of response.headers.getSetCookie()) {
              appendResponseHeader(event, "set-cookie", cookie);
            }
            newSession = response._data as typeof session.value;
          } else {
            console.log("[heyapi] refresh via client $fetch");
            newSession = await $fetch<typeof session.value>("/api/refresh-token", {
              method: "POST",
              credentials: "include",
              timeout: 15_000,
            });
          }

          if (newSession) {
            session.value = newSession;
          }

          lastRefreshTime = Date.now();
          console.log("[heyapi] refresh success", { newToken: newSession?.token?.accessToken?.slice(0, 8) });
        } catch (error: unknown) {
          const transient = isTransientRefreshError(error);
          console.log("[heyapi] refresh failed", { transient, error: (error as Error)?.message });
          if (!transient) {
            await handleSessionLoss();
          }
          throw error;
        } finally {
          setTimeout(() => {
            isRefreshing = false;
            refreshPromise = null;
          }, 100);
        }
      })();

      return refreshPromise;
    };

    const authAdapter: AuthAdapter = {
      getAccessToken: () => (sessionLost ? undefined : session.value?.token?.accessToken),
      refreshToken,
    };

    configureClient(isiaClient, { baseURL: config.public.ISIA_API_URL, auth: authAdapter });
    configureClient(backofficeClient, { baseURL: config.public.BACKOFFICE_API_URL, auth: authAdapter });

    return { provide: { refreshToken } };
  },
});
